import { Funnel, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type FC } from "react";
import type { DateRange } from "react-day-picker";

import type { OptionItem, ResetOptionItem } from "@/api/admin";
import { CustomButton } from "@/components/atoms";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { commonIconProps16 } from "@/utils/constants";
import { formattedTimeRange } from "@/utils/formattedTimeRange";

import FilterWrapper from "./components/FilterWrapper";
import SelectedCategoryBadge from "./components/SelectedCategoryBadge";
import AmountSubMenu from "./subMenu/AmountSubMenu";
import CategorySearchSubMenu from "./subMenu/CategorySearchSubMenu";
import DateRangeSubMenu from "./subMenu/DateRangeSubMenu";
import MainSubMenu from "./subMenu/MainSubMenu";
import RoleSubMenu from "./subMenu/RoleSubMenu";
import SearchInputSubMenu from "./subMenu/SearchInputSubMenu";
import StatusSubMenu from "./subMenu/StatusSubMenu";
import type { StatusVariants, SubMenuConfig } from "./types";

const isEmpty = (obj: object) => Object.keys(obj).length === 0;

export const statusVariantMap: StatusVariants = {
  Approved: { badge: "success", dot: "#65A30D" },
  "Waiting for approval": { badge: "info", dot: "#D97706" },
  Declined: { badge: "warning", dot: "#DC2626" },
};

export type SubMenuItem = string | OptionItem;

interface DropDownFilterProps {
  menuList: string[];
  subMenus: Record<string, SubMenuItem[]>;
  statusVariant?: StatusVariants;
  subMenuRegistry: Record<string, SubMenuConfig>;
  filterButtonSize?: string;
  handleSetFilter?: (selected: Record<string, string[] | OptionItem[]>) => void;
  initialSelectedMap?: Record<string, string[]>;
  initialSelectedObjectMap?: Record<string, OptionItem[]>;
  initialMetaMap?: Record<string, DateRange | AmountRange>;
  resetObject?: ResetOptionItem;
}

type AmountRange = {
  from?: string;
  to?: string;
};

const DropDownFilterMenu: FC<DropDownFilterProps> = ({
  menuList,
  subMenus,
  statusVariant = statusVariantMap,
  subMenuRegistry,
  filterButtonSize = "h-8",
  initialSelectedMap = {},
  initialSelectedObjectMap = {},
  initialMetaMap = {},
  handleSetFilter,
  resetObject,
}) => {
  const [selectedMap, setSelectedMap] =
    useState<Record<string, string[]>>(initialSelectedMap);
  const [selectedObjectMap, setSelectedObjectMap] = useState<
    Record<string, OptionItem[]>
  >(initialSelectedObjectMap);
  const [selectedMetaMap, setSelectedMetaMap] =
    useState<Record<string, DateRange | AmountRange>>(initialMetaMap);

  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const hasSelections = Object.values(selectedMap).some(arr => arr.length > 0);

  const hasUserInteracted = useRef(false);

  useEffect(() => {
    if (hasUserInteracted.current || isEmpty(initialSelectedMap)) {
      return;
    }

    if (!isEmpty(initialSelectedMap)) {
      setSelectedMap(prev => ({
        ...prev,
        ...initialSelectedMap,
      }));
      setSelectedObjectMap(prev => ({
        ...prev,
        ...initialSelectedObjectMap,
      }));
      setSelectedMetaMap(prev => ({
        ...prev,
        ...initialMetaMap,
      }));
    }
  }, [initialSelectedMap, initialSelectedObjectMap, initialMetaMap]);

  const handleSetFilterRef = useRef(handleSetFilter);
  useEffect(() => {
    handleSetFilterRef.current = handleSetFilter;
  });

  useEffect(() => {
    if (!hasUserInteracted.current) {
      return;
    }

    handleSetFilterRef.current?.({
      ...selectedMap,
      ...selectedObjectMap,
    });
  }, [selectedMap, selectedObjectMap]);

  const toggleSearchItem = (category: string, item: OptionItem) => {
    hasUserInteracted.current = true;
    const isSingle = subMenuRegistry[category]?.isSingleSelect;

    setSelectedObjectMap(prev => {
      const current = prev[category] || [];
      const exists = current.some(i => i.id === item.id);

      let updated;
      if (item.id === resetObject?.id) {
        updated = exists ? [] : [item];
      } else if (isSingle) {
        updated = exists ? [] : [item];
      } else {
        const filteredCurrent = current.filter(i => i.id !== resetObject?.id);
        updated = exists
          ? filteredCurrent.filter(i => i.id !== item.id)
          : [...filteredCurrent, item];
      }

      return { ...prev, [category]: updated };
    });

    setSelectedMap(prev => {
      const current = prev[category] || [];
      const exists = current.includes(item.name);

      let updated;
      if (item.id === resetObject?.id) {
        updated = exists ? [] : [item.name];
      } else if (isSingle) {
        updated = exists ? [] : [item.name];
      } else {
        const filteredCurrent = current.filter(i => i !== resetObject?.name);
        updated = exists
          ? filteredCurrent.filter(i => i !== item.name)
          : [...filteredCurrent, item.name];
      }

      return { ...prev, [category]: updated };
    });
  };

  const toggleItem = (category: string, item: string) => {
    hasUserInteracted.current = true;
    const isSingle = subMenuRegistry[category]?.isSingleSelect;

    setSelectedMap(prev => {
      const current = prev[category] || [];

      let updated;
      if (isSingle) {
        updated = current.includes(item) ? [] : [item];
      } else {
        updated = current.includes(item)
          ? current.filter(i => i !== item)
          : [...current, item];
      }

      return { ...prev, [category]: updated };
    });
  };

  const handleMenuClick = (item: string) => {
    const isItemDisabled = subMenuRegistry[item]?.disabled;

    if (isItemDisabled) return;

    if (subMenus[item]) {
      setActiveMenu(item);
      setTimeout(() => setIsOpen(true), 0);
    } else {
      toggleItem(item, item);
    }
  };

  const clearCategory = (category: string) => {
    hasUserInteracted.current = true;
    setSelectedMap(prev => {
      const updated = { ...prev };
      delete updated[category];
      return updated;
    });

    setSelectedMetaMap(prev => {
      const updated = { ...prev };
      delete updated[category];
      return updated;
    });

    setSelectedObjectMap(prev => {
      const updated = { ...prev };
      delete updated[category];
      return updated;
    });
  };

  const clearAll = () => {
    hasUserInteracted.current = true;
    setSelectedMap({});
    setSelectedMetaMap({});
    setSelectedObjectMap({});
  };

  const openSubMenu = (category: string) => {
    setActiveMenu(category);
    setIsOpen(false);
    setTimeout(() => setIsOpen(true), 0);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setActiveMenu(null);
    }
  };

  const config = activeMenu ? subMenuRegistry[activeMenu] : null;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={open => handleOpenChange(open)}>
        <DropdownMenuTrigger asChild>
          <CustomButton
            variant="filter"
            className={cn(
              "px-[10px]!",
              isOpen || (hasSelections && "bg-gray-100"),
              filterButtonSize
            )}
            data-testid="filter-trigger"
          >
            <Funnel {...commonIconProps16} />
            <span className="text-xs">Filter</span>
          </CustomButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-fit" align="start">
          {config?.type !== "date" && (
            <DropdownMenuLabel data-testid="filter-label">
              {activeMenu ?? "Filter"}
            </DropdownMenuLabel>
          )}
          {!activeMenu && (
            <MainSubMenu
              menuList={menuList}
              handleMenuClick={handleMenuClick}
              selectedMap={selectedMap}
              subMenuRegistry={subMenuRegistry}
            />
          )}

          {config?.type === "search" && (
            <CategorySearchSubMenu
              items={(subMenus[activeMenu!] || []) as OptionItem[]}
              selectedItems={selectedObjectMap[activeMenu!] || []}
              toggleItem={item => toggleSearchItem(activeMenu!, item)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              placehoder={config.placeholder ?? "Search"}
              isSingleSelect={config.isSingleSelect}
            />
          )}

          {config?.type === "search_input" && (
            <SearchInputSubMenu
              category={activeMenu!}
              initialValue={selectedMap[activeMenu!]?.[0] || ""}
              onApply={(category, value) => {
                hasUserInteracted.current = true;
                setSelectedMap(prev => ({
                  ...prev,
                  [category]: value ? [value] : [],
                }));
              }}
              placeholder={config.placeholder}
            />
          )}

          {config?.type === "role" && (
            <RoleSubMenu
              items={(subMenus[activeMenu!] || []) as string[]}
              selectedItems={selectedMap[activeMenu!] || []}
              toggleItem={item => toggleItem(activeMenu!, item)}
            />
          )}

          {config?.type === "status" && (
            <StatusSubMenu
              category={activeMenu!}
              subMenus={subMenus as Record<string, string[]>}
              toggleItem={toggleItem}
              statusVariantMap={statusVariant}
              selectedMap={selectedMap}
            />
          )}

          {config?.type === "amount" && (
            <AmountSubMenu
              category={activeMenu!}
              range={config.range}
              initialValue={selectedMap[activeMenu!]?.[0] || ""}
              initialRange={
                config.range
                  ? (selectedMetaMap[activeMenu!] as AmountRange | undefined)
                  : undefined
              }
              onApply={(category, value) => {
                hasUserInteracted.current = true;
                if (typeof value === "string") {
                  setSelectedMap(prev => ({
                    ...prev,
                    [category]: [value],
                  }));
                } else {
                  let formatted = "";

                  if (value.from && value.to) {
                    formatted = `${value.from}-${value.to}`;
                  } else if (value.to) {
                    formatted = `0-${value.to}`;
                  } else if (value.from) {
                    formatted = value.from;
                  }

                  if (!formatted) return;

                  setSelectedMap(prev => ({
                    ...prev,
                    [category]: [formatted],
                  }));

                  setSelectedMetaMap(prev => ({
                    ...prev,
                    [category]: value,
                  }));
                }
              }}
              setIsOpen={setIsOpen}
            />
          )}

          {config?.type === "date" && (
            <DateRangeSubMenu
              category={activeMenu!}
              initialRange={
                selectedMetaMap[activeMenu!] as DateRange | undefined
              }
              disabledDays={config.disabledDays}
              onApply={(category, range) => {
                hasUserInteracted.current = true;
                const formatted = formattedTimeRange(range);
                setSelectedMap(prev => ({
                  ...prev,
                  [category]: [formatted],
                }));

                setSelectedMetaMap(prev => ({
                  ...prev,
                  [category]: range,
                }));
              }}
            />
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {Object.entries(selectedMap).map(([category, selected]) =>
        selected.length > 0 ? (
          <SelectedCategoryBadge
            key={category}
            category={category}
            selected={selected}
            onClear={clearCategory}
            onOpen={openSubMenu}
            statusVariantMap={statusVariant}
            classes={filterButtonSize}
          />
        ) : null
      )}

      {hasSelections && (
        <FilterWrapper classes={filterButtonSize}>
          <button
            className="flex items-center gap-[2px] cursor-pointer"
            onClick={clearAll}
            data-testid="filter-reset-button"
          >
            <X size={16} className="size-[16px] mr-2" />
            <span>Reset</span>
          </button>
        </FilterWrapper>
      )}
    </>
  );
};

export default DropDownFilterMenu;
